import {Check, FileUploadRounded} from "@mui/icons-material";
import React, {useCallback, useMemo, useState} from "react";
import {IconButton} from "../../../features/management-v2/import/inputs";
import {usePrettyFileSize} from "../../../lib/files/usePrettyFileSize";
import {useFileSelect} from "../hooks/useFileSelect";
import BasicText from "../../../components/form-v2/basic-text";
import SaveIcon from "@mui/icons-material/Save";
import {platformAPI} from "../../../api/axiosConfig";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useSiren} from "../SirenContext";
import {useUploadSirenAudio} from "../hooks/useUploadSirenAudio";

export const AudioUpload: React.FC = () => {
    const {navigateWithClientID} = useNavigateWithClientID();
    const {clientID} = useSiren();
    const {upload, uploading, progress, error: uploadError} = useUploadSirenAudio();

    const [saveError, setSaveError] = useState<string | null>(null);
    const [name, setName] = useState("");

    const {selectedFile, invalidFile, fileInputRef, activateFileSelect, handleFileChange, fileUrl, accept} =
        useFileSelect({allowedTypes: ["mp3", "wav", "m4a"]});

    const fileSize = usePrettyFileSize(selectedFile);

    const text = useMemo(() => {
        if (invalidFile) return "Invalid File";
        return selectedFile ? `${selectedFile.name} (${fileSize})` : "Select Audio File";
    }, [selectedFile, invalidFile, fileSize]);

    const icon = useMemo(() => selectedFile ? <Check/> : <FileUploadRounded/>, [selectedFile]);

    const canSave = !!(name.trim() && selectedFile && !invalidFile);

    const save = useCallback(async () => {
        if (!clientID || !selectedFile) return;
        setSaveError(null);

        const uploadID = await upload(selectedFile, name);
        if (!uploadID) return;

        try {
            await platformAPI.post("api/siren/audio-files", {uploadID, name, clientID});
            navigateWithClientID("/siren/audio?subPage=files");
        } catch {
            setSaveError("error saving audio file");
        }
    }, [name, selectedFile, clientID, upload]);

    return (
        <div className="bg-white shadow-md p-4 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">Upload Audio File</h1>
            <BasicText label="Name" value={name} onChange={setName} required/>
            <div className="flex flex-col gap-4">
                <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden"/>
                <IconButton onClick={activateFileSelect} text={text} icon={icon}/>
                {fileUrl && <audio src={fileUrl} controls className="w-full"/>}
                {canSave && <IconButton onClick={save} text="Upload" icon={<SaveIcon/>} loading={uploading}/>}
                {uploading && (
                    <div className="w-full bg-light-light-grey rounded-full h-2">
                        <div className="bg-blue h-2 rounded-full transition-all duration-200" style={{width: `${progress}%`}}/>
                    </div>
                )}
                {uploadError && <p className="text-center text-red">{uploadError}</p>}
                {saveError && <p className="text-center text-red">{saveError}</p>}
            </div>
        </div>
    );
};
